// Phase 129 Plan 04 Task 1 — Hero section (DESIGN.md dark theme).
//
// Two-column hero: copy on the left, phone video mockup on the right (md+).
// Meta Blue radial glow behind the headline gives the "immersive product
// moment" depth per DESIGN.md, and a separate softer halo sits behind the
// phone. Phone column is hidden below md so mobile gets the copy + CTA only.
//
// Below the hero, a "Meta Business Partners" trust strip with hairline
// gradient dividers on each side anchors credibility before the demo section.
//
// Per CLAUDE.md feedback_button_title_case, button labels use Title Case
// ("Start Free", not "Start free"). Per feedback_no_em_dashes, no em dashes
// in user-facing copy.
//
// Signup CTA goes through buildHandoffUrl('') so the URL resolves to the
// correct app surface per environment (staging vs production vs local).
//
// Assets: the phone-frame reel + poster are imported below so Vite hashes
// them; meta-logo.png (the Meta wordmark for the partners strip) stays in
// web/public/ since it is referenced by path.
import { useRef, useState } from "react";
import { Check, Copy, Play } from "lucide-react";
import { emit } from "../lib/analytics";
import { buildHandoffUrl } from "../lib/handoff";
import { CHANNEL_COPY, type Channel } from "../lib/channel-copy";
// AIT-411 — bridge reel shown in the hero phone mock, encoded at 480px wide
// (2x the w-60 mock). The poster is NOT frame 0 (that's a blank grid) — it's
// the hand-picked "Bridge To Your Channels" title card around 26s in, so the
// mock reads as branded before playback starts.
//
// AIT-448 — imported rather than read from web/public so Vite emits both under
// /assets/ with a content hash. react-router-serve caches that directory
// `immutable, max-age=1y`; bare public/ files come back max-age=0 and
// revalidate on every page load.
import REEL_MP4 from "../assets/hero-reel.mp4";
import REEL_POSTER from "../assets/hero-reel.jpg";

// AIT-215: static agent-setup instruction block (Decision 4 of the AIT-213
// spec). Mirrors the dashboard variant of the backend instruction template
// with two differences: production URLs hardcoded (marketing site is prod
// only) and no embedded bootstrap code — the agent asks the visitor for the
// code they receive by email.
const AGENT_INSTRUCTIONS = `Set up HookMyApp for me. Follow these steps:

Before you start, one rule for network errors. If a command below fails with ENOTFOUND, EAI_AGAIN, ECONNREFUSED, ENETUNREACH, EHOSTUNREACH, ETIMEDOUT or ECONNRESET, you are probably in a sandbox without internet access, so ask me to allow network access. Then:
- ENOTFOUND, EAI_AGAIN, ECONNREFUSED, ENETUNREACH or EHOSTUNREACH: the request never left this machine, so run the same command again, whatever it is.
- ETIMEDOUT or ECONNRESET on a hookmyapp command that signs in or sends or checks a code (login, alerts phone set, alerts phone verify): do not rerun it, because the request may have reached HookMyApp and these steps cannot be repeated safely. For alerts phone, run hookmyapp alerts phone status to see whether it took effect and continue from there. For login, stop and tell me the exact error; do not continue on a sign-in you cannot confirm. The one exception is the step 4 code request (hookmyapp login --email <my email> --json): run that again, even though step 4 says exactly once. While the first code is still valid it returns the same registrationId and does not send another code.
- ETIMEDOUT or ECONNRESET on any other command (npm, npx, or any other hookmyapp command): run it again.
Only if a command still fails with network access allowed, follow that step's own failure rule.

1. Check prerequisites. You need Node.js 20 or newer (npm ships with it):
   node -v
   If node is missing or below v20, STOP: every step below is a CLI command, so this setup cannot run without it. Tell me to install the current LTS from https://nodejs.org and re-run. Tell me the alternative too, in case I do not want Node on this machine: I can create the account at https://app.hookmyapp.com and drive it from the hosted MCP server (https://api.hookmyapp.com/mcp) or the REST API with an API key, no CLI involved.

2. Install the HookMyApp agent skills globally so this and future agent sessions know how to finish or recover the setup:
   npx skills add hookmyapp/agent-skills --all --global
   If the command fails, continue anyway: step 5 runs the same install again and usually repairs it. Only if it fails there too, tell me to run the skills command myself.

3. Install the HookMyApp CLI. Run this even if hookmyapp is already installed, so it upgrades to the latest version:
   npm install -g @gethookmyapp/cli
   On macOS or Linux, if it fails with EACCES (permission denied) on npm's global folder (the path in the error is inside the output of npm config get prefix, usually /usr/local), do not use sudo. Move npm's global folder into my home directory and install again:
   npm config set prefix ~/.npm-global
   npm install -g @gethookmyapp/cli
   Then put ~/.npm-global/bin on my PATH for new terminals: add export PATH="$HOME/.npm-global/bin:$PATH" to my shell startup file (~/.zshrc for zsh; for bash, the existing file my terminal actually loads, such as ~/.bash_profile, ~/.bash_login, ~/.profile or ~/.bashrc, and never create a new ~/.bash_profile; for fish, csh or another shell, use that shell's own syntax instead, such as fish_add_path ~/.npm-global/bin) so hookmyapp stays on my PATH in new terminals, and tell me you did. If this install fails too, or EACCES names any other path, STOP and tell me the exact error.
   If hookmyapp is then not found (command not found), the install still worked: agent shells often do not read my shell startup file. Do not stop. On macOS or Linux, run npm prefix -g and use the folder it prints plus /bin/hookmyapp as the full path for every hookmyapp command in this setup.

4. Sign me in. This also creates my free HookMyApp account if I don't have one yet. Look for email addresses that are likely mine (git config user.email, prior context). If you found any, ask me: "Your free HookMyApp account needs an email address. Should I use <the email(s) you found>, or a different one?" If you found none, ask me: "To set up your free HookMyApp account I need an email address. Which one should I use?" Either way, keep it to that one question, with no commands and no mention of where you looked. Once I confirm, run:
   hookmyapp login --email <my email> --json
   Run that initiation command exactly once. Save its registrationId and do not request another code. A 6-digit code is sent to my email. Tell me: "I just sent a 6-digit sign-in code to <email>. Paste it here so I can finish setting up your HookMyApp account." Any unexpired code sent to that same email during this 10-minute login window is valid. Complete with:
   hookmyapp login --email <my email> --registration-id <the saved registrationId> --otp <the 6-digit code> --json

   Use the hookmyapp CLI for everything until the MCP tools are live (see the HookMyApp skill).

5. Set yourself up to use HookMyApp directly:
   hookmyapp agent setup
   This connects the HookMyApp MCP server to every coding agent installed on this machine, with access already granted, so no agent needs a separate sign-in. It also re-runs the skills install from step 2, which repairs it if that step failed. It prints one line per agent naming the restart that agent needs. Do only what it names for you.
   Do not try to prove this worked by calling a HookMyApp MCP tool now. You loaded your MCP servers when this session started, so any hookmyapp tool you can see is the old configuration and calling it proves nothing. Tell me to restart you instead.

6. Confirm the setup:
   hookmyapp doctor
   If doctor reports an error or exits non-zero, STOP and tell me what failed.

7. Set my alert phone so HookMyApp can reach me when the integration breaks later:
   hookmyapp alerts phone status
   If that already shows a verified number, skip this step. Otherwise tell me why before asking: HookMyApp texts this number (WhatsApp or SMS) when my webhooks start failing, a channel disconnects, or my plan limit is reached, so I find out even when I am not watching the logs. Then ask me for the number and run:
   hookmyapp alerts phone set <the number I give you>
   hookmyapp alerts phone verify <the 6-digit code I read back to you>
   Never invent the number or the code, both come from me. If I decline, skip it and continue, nothing downstream depends on it.

   Only after doctor succeeds, tell me setup is done. Also tell me to restart you, which is what makes the HookMyApp MCP tools available. The CLI already covers everything in the meantime.

If anything fails and its step does not say how to recover, STOP, preserve the registrationId, and tell me the exact error. Do not run the initiation command again or request another code unless all existing codes are expired or locked and I explicitly approve another email. The network-error rule at the top is the only exception: its rerun of the code request reuses the same code.
`;

export function Hero({ channel = "both" }: { channel?: Channel }) {
  const ctaHref = buildHandoffUrl("");
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const reelRef = useRef<HTMLVideoElement>(null);
  const [reelPlaying, setReelPlaying] = useState(false);

  function toggleReel() {
    const v = reelRef.current;
    if (!v) return;
    if (reelPlaying) {
      v.pause();
      setReelPlaying(false);
    } else {
      v.muted = false;
      v.play().catch(() => setReelPlaying(false));
      setReelPlaying(true);
    }
  }

  async function copyInstructions() {
    emit("cta_clicked", {
      cta_location: "hero",
      cta_text: "Copy AI Instruction",
      destination_url: "",
    });
    try {
      await navigator.clipboard.writeText(AGENT_INSTRUCTIONS);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    setTimeout(() => setCopyState("idle"), 2000);
  }
  return (
    <section
      data-testid="hero"
      className="relative overflow-x-clip bg-black px-6 pt-20 pb-16 md:pt-28 md:pb-24"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 -z-0 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-[#0064E0]/15 blur-[140px]"
      />
      <div className="relative mx-auto flex max-w-4xl flex-col items-center lg:max-w-6xl lg:flex-row lg:items-center lg:justify-center lg:gap-20">
        {/* Phone: below the copy on mobile, left column with a slight 3D tilt
            on desktop (Kapso-style hero) */}
        <div className="relative order-last mt-14 w-fit shrink-0 lg:order-first lg:mt-0">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0064E0]/10 blur-[100px]"
          />
          {/* Fixed aspect + width reserve the box, so no layout shift while the video loads */}
          <div className="relative aspect-[9/19.5] w-60 rounded-[3rem] border-8 border-neutral-900 bg-neutral-900 shadow-2xl ring-1 ring-white/15">
            <div className="absolute left-1/2 top-2.5 z-10 h-6 w-24 -translate-x-1/2 rounded-full bg-neutral-900" />
            {/* Reel is exported at 1080x2340 (9:19.5) to match the screen
                exactly; cover only absorbs subpixel rounding */}
            <video
              ref={reelRef}
              src={REEL_MP4}
              poster={REEL_POSTER}
              className="h-full w-full rounded-[2.5rem] bg-black object-cover"
              loop
              playsInline
              preload="metadata"
            />
            <button
              type="button"
              onClick={toggleReel}
              aria-label={reelPlaying ? "Pause video" : "Play video"}
              className="absolute inset-0 z-20 flex items-center justify-center rounded-[2.5rem]"
            >
              {!reelPlaying && (
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition hover:scale-105">
                  <Play
                    aria-hidden="true"
                    fill="currentColor"
                    className="h-7 w-7 translate-x-0.5 text-black"
                  />
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="text-center lg:text-left">
        <h1
          className="text-5xl md:text-[64px] font-medium leading-[1.16] text-white"
          style={{ letterSpacing: "-0.02em" }}
        >
          {CHANNEL_COPY[channel].heroHeadline}
        </h1>
        <p className="mt-6 text-2xl md:text-[28px] font-light tracking-tight leading-[1.21] text-white/85">
          Sell more, engage better, and grow your audience
        </p>
        <div className="mt-6 flex flex-col items-center gap-4 lg:items-start">
          <p className="text-lg text-white/75">
            Let Claude Code, Codex, or any AI set everything up for you. It's
            free.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 lg:justify-start">
          <button
            type="button"
            onClick={copyInstructions}
            className="inline-flex items-center justify-center gap-2 rounded-[100px] bg-[#0064E0] hover:bg-[#0143B5] active:bg-[#004BB9] px-8 py-4 text-base font-medium text-white transition"
          >
            {copyState === "copied"
              ? "Copied!"
              : copyState === "failed"
                ? "Copy Failed"
                : "Copy AI Instruction"}
            {copyState === "copied" ? (
              <Check aria-hidden="true" className="h-5 w-5" />
            ) : (
              <Copy aria-hidden="true" className="h-5 w-5" />
            )}
          </button>
          <a
            href={ctaHref}
            onClick={() =>
              emit("cta_clicked", {
                cta_location: "hero",
                cta_text: "or do it yourself",
                destination_url: ctaHref,
              })
            }
            className="text-sm text-white/60 underline underline-offset-4 hover:text-white transition"
          >
            or do it yourself
          </a>
          </div>
        </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-50 translate-y-1/2 flex items-center justify-center gap-1 px-6">
        <div className="h-px flex-1 bg-[linear-gradient(to_right,_transparent_0%,_rgba(255,255,255,0.18)_30%,_rgba(255,255,255,0.18)_70%,_transparent_100%)]" />
        <div className="flex items-center gap-2.5">
          <img
            src="/meta-logo.png"
            alt="Meta"
            className="inline-block h-5 w-auto align-middle"
          />
          <span className="text-base font-semibold tracking-wide text-white/80">
            Meta Business Partners
          </span>
        </div>
        <div className="h-px flex-1 bg-[linear-gradient(to_right,_transparent_0%,_rgba(255,255,255,0.18)_30%,_rgba(255,255,255,0.18)_70%,_transparent_100%)]" />
      </div>
    </section>
  );
}
